import { Component, Vue } from "vue-property-decorator";
import { AxiosError, AxiosResponse } from "axios";
import axios from "@/utils/axios";
import { IBranch } from "@/interfaces/IBranch";
import { IBasicNamedDTO } from "@/interfaces/IBasicNamedDTO";
import { ILicenseClassGroup } from "@/interfaces/ILicenseClassGroup";
import { ulrStringify } from "@/utils/UrlUtils";
import createCancelableRequest from "@/utils/axiosCancelable";

const URL = "license-classes";

@Component
export default class LicenseClassRequestMixin extends Vue {
  protected licenseClassCancelableRequest = createCancelableRequest(LicenseClassRequestMixin.name);

  public licenseClasses: Array<IBranch> = [];
  public licenseClassesLoading = false;
  public licenseClassesSuccess = false;
  public licenseClassesError: Record<any, any> | null = null;

  public async findAllBaseLicenseClass(params: any = {}): Promise<void> {
    this.licenseClassesLoading = true;
    this.licenseClassesSuccess = false;
    this.licenseClassesError = null;

    const args = ulrStringify(params);

    return await axios
      .get(`${URL}/base-license-classes${args}`)
      .then((response: AxiosResponse) => {
        this.licenseClasses = response.data;
        this.licenseClassesSuccess = true;
      })
      .catch((error: AxiosError) => {
        this.licenseClassesError = error.response?.data;
      })
      .finally(() => {
        this.licenseClassesLoading = false;
      });
  }

  public licenseClassesForCurrentDrivingSchool: Array<IBasicNamedDTO> = [];
  public licenseClassesForCurrentDrivingSchoolLoading = false;

  public async findLicenseClassesForCurrentDrivingSchool(): Promise<void> {
    this.licenseClassesForCurrentDrivingSchoolLoading = true;

    return await axios
      .get(`${URL}/driving-school/current/named`)
      .then((response: AxiosResponse) => {
        this.licenseClassesForCurrentDrivingSchool = response.data;
      })
      .finally(() => {
        this.licenseClassesForCurrentDrivingSchoolLoading = false;
      });
  }

  public licenseClassGroupsForCurrentDrivingSchool: Array<ILicenseClassGroup> = [];
  public licenseClassGroupsForCurrentDrivingSchoolLoading = false;

  public async findLicenseClassGroupsForCurrentDrivingSchool(): Promise<void> {
    this.licenseClassGroupsForCurrentDrivingSchoolLoading = true;

    return await axios
      .get(`${URL}/driving-school/current/named/grouped`)
      .then((response: AxiosResponse) => {
        this.licenseClassGroupsForCurrentDrivingSchool = response.data;
      })
      .finally(() => {
        this.licenseClassGroupsForCurrentDrivingSchoolLoading = false;
      });
  }

  public licenseClassesAsExistingClasses: Array<IBranch> = [];
  public licenseClassesAsExistingClassesLoading = false;
  public licenseClassesAsExistingClassesSuccess = false;
  public licenseClassesAsExistingClassesError: Record<any, any> | null = null;

  // Classes that can be used in Existing Classes Dropdown
  public async findLicenseClassesAsExistingClasses(params: any = {}): Promise<void> {
    this.licenseClassesAsExistingClassesLoading = true;
    this.licenseClassesAsExistingClassesSuccess = false;
    this.licenseClassesAsExistingClassesError = null;

    return await axios
        .get(`${URL}/as-existing-classes`)
        .then((response: AxiosResponse) => {
          this.licenseClassesAsExistingClasses = response.data;
          this.licenseClassesAsExistingClassesSuccess = true;
        })
        .catch((error: AxiosError) => {
          this.licenseClassesAsExistingClassesError = error.response?.data;
        })
        .finally(() => {
          this.licenseClassesAsExistingClassesLoading = false;
        });
  }

  protected relatedDocuments = [];
  private relatedDocumentsLoadingQueue = 0;
  protected relatedDocumentsSuccess = false;
  protected relatedDocumentsError = null;

  public get relatedDocumentsLoading() {
    return this.relatedDocumentsLoadingQueue > 0;
  }

  public async getRelatedDocuments(licenseClass: string): Promise<void> {
    this.relatedDocumentsLoadingQueue++;
    this.relatedDocumentsSuccess = false;
    this.relatedDocumentsError = null;

    return await this.licenseClassCancelableRequest
      .makeRequest({ url: `${URL}/related-documents?licenseClass=${licenseClass}`, method: "get" })
      .then((response: AxiosResponse) => {
        this.relatedDocuments = response.data;
        this.relatedDocumentsSuccess = true;
      })
      .catch((error: AxiosError) => {
        this.relatedDocumentsError = error.response?.data;
      })
      .finally(() => {
        this.relatedDocumentsLoadingQueue--;
      });
  }

  public destroyed() {
    this.licenseClassCancelableRequest.cancelPreviousRequest();
  }
}
